import {
  Button,
  Box,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  FormControl,
  VStack,
  Link,
  Text,
  Checkbox,
  Divider,
  Collapse,
  useColorModeValue,
  Heading,
  Flex,
} from '@chakra-ui/react';
import { useDisclosure } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { useState, useContext, useEffect, useRef } from 'react';
import { HttpContext } from '../../../context/HttpContext';
import { MeContext } from '../../../context/MeContext';
import PassDownloadTemplatesListContext from '../../../store/client/PassDownloadTemplateListContext';
import {
  CustomReactSelect,
  formatOptionLabelFilterPassTemplate,
} from '../../common/CustomReactSelect';
import { useReportAndToastApiError } from '../../../hooks/useReportAndToastApiError';
import { handleCheckboxFilter } from '../../../utils/handleCheckboxFilter.js';
import FilterButton from '../../common/FilterButton';
import ClearFiltersButton from '../../common/ClearFiltersButton';

function PassDownloadTemplatesListFilter() {
  const reportAndToastApiError = useReportAndToastApiError();
  const { authAxios } = useContext(HttpContext);
  const passDownloadTemplatesListCtx = useContext(
    PassDownloadTemplatesListContext
  );
  const [passTemplatesList, setPassTemplatesList] = useState([]);
  const [totalActiveFilters, setTotalActiveFilters] = useState(0);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isOpenPassTemplate, onToggle: onTogglePassTemplate } =
    useDisclosure();
  const [selectedPassTemplate, setSelectedPassTemplate] = useState('');
  const modeBoxBg = useColorModeValue('white', 'secondaryDark');
  const modeTextBg = useColorModeValue('white', 'primaryDark06');
  const modeLabelBg = useColorModeValue('white', 'primaryDark03');
  const modeBorderColor = useColorModeValue('primaryBackground', 'primaryDark');
  const modeLabelColor = useColorModeValue('secondaryDark', 'white');
  const meCtx = useContext(MeContext);
  const { filterEntity } = meCtx.state;

  useEffect(() => {
    const getData = async () => {
      try {
        let response;
        if (filterEntity) {
          response = await authAxios.get(
            `entities/${filterEntity.uuid}/passes/templates?skipImages=true&fields=name`
          );
        } else {
          response = await authAxios.get(
            `passes/templates?skipImages=true&fields=name`
          );
        }
        const data = response?.data?.data;
        data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        let templates = [];
        if (data.length === 0) {
          return;
        }
        data.forEach((item) => {
          templates.push({
            label: item.name,
            value: item,
          });
        });
        setPassTemplatesList(templates);
        // if no filter is set always return to All pass templates default
      } catch (error) {
        reportAndToastApiError(error);
      }
    };

    getData();

    return () => {
      setPassTemplatesList([]);
    };
  }, [authAxios, filterEntity]);

  const filterEntityRef = useRef(filterEntity);

  useEffect(() => {
    if (filterEntity !== filterEntityRef.current) {
      filterEntityRef.current = filterEntity;
      onResetAll();
    }
  }, [filterEntity]);

  const { register, handleSubmit, reset, setValue, getValues } = useForm();

  const onSubmit = async (values) => {
    // on submit alter values object and add status from useState
    values.passTemplate = selectedPassTemplate
      ? selectedPassTemplate.value.uuid
      : '';
    passDownloadTemplatesListCtx.updateFilterValues(values);

    let countableFilterValues = [];
    Object.entries(values).forEach(([key, value]) => {
      if (!key.startsWith('by') && value !== false) {
        countableFilterValues = countableFilterValues.concat(value);
      }
    });
    setTotalActiveFilters(
      countableFilterValues.filter((val) => val.length !== 0).length
    );
    onClose();
  };

  useEffect(() => {
    setValue('passTemplate', selectedPassTemplate.passTemplate);
  }, [selectedPassTemplate, setValue]);

  const onResetAll = async () => {
    reset();
    setValue('byPassTemplate', false);
    passDownloadTemplatesListCtx.updateFilterValues({
      passTemplate: '',
    });
    setTotalActiveFilters(0);
    onClose();
    isOpenPassTemplate && onTogglePassTemplate();
    setSelectedPassTemplate('');
  };

  return (
    <Flex
      alignSelf="flex-end"
      justify="flex-start"
      flexDirection={{ base: 'row-reverse', sm: 'row' }}
      flexGrow={{ base: '1', sm: 'unset' }}
    >
      {totalActiveFilters > 0 && <ClearFiltersButton onResetAll={onResetAll} />}
      <FilterButton onOpen={onOpen} totalActiveFilters={totalActiveFilters} />
      <Drawer onClose={onClose} isOpen={isOpen}>
        <DrawerOverlay />
        <form onSubmit={handleSubmit(onSubmit)}>
          <DrawerContent bg={modeBoxBg}>
            <DrawerCloseButton />
            <DrawerHeader
              borderBottomWidth="1px"
              borderBottomColor={modeBorderColor}
              pt={{ base: '16px', lg: '56px' }}
              bg={modeTextBg}
            >
              <Heading>Filter pages</Heading>
            </DrawerHeader>
            <DrawerBody p="0" bg={modeLabelBg}>
              <VStack spacing={0}>
                {/* by pass template */}
                <FormControl px="24px" mt="12px">
                  <Checkbox
                    id="byPassTemplate"
                    {...register('byPassTemplate')}
                    onChange={() =>
                      handleCheckboxFilter({
                        getValues,
                        reset,
                        setValue,
                        toggleFunction: onTogglePassTemplate,
                        fieldToClear: 'passTemplate',
                        clearCallback: () => {
                          setSelectedPassTemplate('');
                        },
                      })
                    }
                  >
                    <Text color={modeLabelColor}>By pass template</Text>
                  </Checkbox>
                  <Collapse in={isOpenPassTemplate} animateOpacity>
                    <CustomReactSelect
                      options={passTemplatesList}
                      placeholder="Select pass template"
                      formatOptionLabel={formatOptionLabelFilterPassTemplate}
                      onChange={(passTemplate) =>
                        setSelectedPassTemplate({
                          label: passTemplate.label,
                          value: passTemplate.value,
                        })
                      }
                      value={selectedPassTemplate}
                      id="passTemplateFilter"
                      passTemplate
                    />
                  </Collapse>
                </FormControl>
                <Box py="12px" w="full">
                  <Divider borderColor={modeBorderColor} />
                </Box>
              </VStack>
            </DrawerBody>
            <DrawerFooter
              borderTopWidth="1px"
              borderTopColor={modeBorderColor}
              bg={modeTextBg}
            >
              <Link
                color={useColorModeValue('secondaryDark08', '#DDDDDD')}
                fontSize="14px"
                onClick={onResetAll}
                mr="30px"
              >
                <Text textStyle="headingFamilyMedium">Reset all</Text>
              </Link>
              <Button
                type="submit"
                alt="Apply filters"
                size="sm"
                px="30px"
                alignSelf="flex-start"
              >
                Apply filters
              </Button>
            </DrawerFooter>
          </DrawerContent>
        </form>
      </Drawer>
    </Flex>
  );
}

export default PassDownloadTemplatesListFilter;
